import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const LS_KEY = "allsale_favorites_local";

function readLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeLocal(ids) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(Array.from(new Set(ids))));
  } catch { /* ignore */ }
}

/**
 * useFavorites — persistent favourites (heart).
 * - Logged in: server-backed (GET /my/favorites returns listing objects).
 *   Local guest hearts are automatically flushed via POST /my/favorites/sync
 *   the first time the user is authenticated.
 * - Logged out: localStorage only. Only listing IDs are stored client-side.
 */
export function useFavorites() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["favorites", user?.id || "guest"],
    queryFn: async () => {
      if (!user) return { listings: [], ids: readLocal() };
      // On first authenticated fetch, flush any guest hearts
      const guest = readLocal();
      if (guest.length > 0) {
        try {
          await sharedApi.post("/classifieds/my/favorites/sync", { listing_ids: guest });
          writeLocal([]);
        } catch { /* server may reject unknown ids — ignore */ }
      }
      const { data } = await sharedApi.get("/classifieds/my/favorites");
      const listings = data?.listings || data?.favorites || [];
      const ids = listings.map((l) => l.id);
      return { listings, ids };
    },
    staleTime: 60_000,
    retry: 0,
  });

  const toggleMut = useMutation({
    mutationFn: async ({ listingId, isFavorite }) => {
      if (!user) {
        const current = readLocal();
        const next = isFavorite
          ? current.filter((x) => x !== listingId)
          : [...current, listingId];
        writeLocal(next);
        return { local: true, ids: next };
      }
      if (isFavorite) {
        await sharedApi.delete(`/classifieds/listings/${listingId}/favorite`);
      } else {
        await sharedApi.post(`/classifieds/listings/${listingId}/favorite`);
      }
      return { local: false };
    },
    onMutate: async ({ listingId, isFavorite }) => {
      await qc.cancelQueries({ queryKey: ["favorites", user?.id || "guest"] });
      const prev = qc.getQueryData(["favorites", user?.id || "guest"]);
      qc.setQueryData(["favorites", user?.id || "guest"], (old) => {
        const base = old || { listings: [], ids: [] };
        const nextIds = isFavorite
          ? base.ids.filter((x) => x !== listingId)
          : Array.from(new Set([...(base.ids || []), listingId]));
        return { ...base, ids: nextIds };
      });
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["favorites", user?.id || "guest"], ctx.prev);
      toast.error(err?.response?.data?.detail || "Couldn't update favourites");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["favorites", user?.id || "guest"] }),
  });

  return {
    ids: q.data?.ids || [],
    listings: q.data?.listings || [],
    isLoading: q.isLoading,
    isError: q.isError,
    refetch: q.refetch,
    toggle: (listingId) => {
      const isFav = (q.data?.ids || []).includes(listingId);
      toggleMut.mutate({ listingId, isFavorite: isFav });
    },
    isMutating: toggleMut.isPending,
    isFavorite: (id) => (q.data?.ids || []).includes(id),
  };
}
